import { useNavigate, useSearchParams } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import sdkServices from "../../../utilities/defaults/sdk/SdkServices";
import SdkContext from "../../../utilities/contexts/sdk/sdkContext";
import AppContext from "../../../utilities/contexts/appContext";
import { Button, Spin } from "antd";
import LoginFormModal from "../../shared/LoginFormModal";
import Api from "../../../utilities/Api/request";
import i18next from "i18next";

const Gate = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const callback = searchParams.get("callback");
  const serviceType = searchParams.get("serviceType");
  const sdkContext = useContext(SdkContext);
  const appContext = useContext(AppContext);
  const navigate = useNavigate();

  const [showWelcome, setShowWelcome] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isValidating, setIsValidating] = useState(true);

  useEffect(() => {
    if (!token || !serviceType) {
      sdkContext.handleLoading(false);
      navigate("/sdk/accessDenied");
      return;
    }

    const validServiceTypes = [sdkServices.BODY_MEASUREMENT, sdkServices.SIZE_MEASUREMENT, sdkServices.AUTO_TAGGING, sdkServices.VISUAL_SEARCH, sdkServices.GARMENT_MEASUREMENT];

    if (!validServiceTypes.includes(serviceType)) {
      localStorage.clear();
      sdkContext.handleLoading(false);
      navigate("/sdk/accessDenied");
      return;
    }

    sdkContext.handleLogin(token, callback || "", serviceType);
    localStorage.setItem("sdk_token", token);
    if (callback) {
      localStorage.setItem("sdk_callback", callback);
    }
    localStorage.setItem("sdk_service_type", serviceType);

    setIsValidating(false);
    setShowWelcome(true);
    sdkContext.handleLoading(false);
  }, []);

  const handleContinueWithoutLogin = () => {
    setShowWelcome(false);
    navigate("/sdk/unifiedService");
  };

  const handleShowLogin = () => {
    setShowLoginModal(true);
  };

  const handleLoginSuccess = async (mainToken: string) => {
    setShowLoginModal(false);
    appContext.handleLoading(true);

    try {
      const response = await Api.get("/api/app/v1/me", {
        headers: {
          "Accept-Language": i18next.language,
          Authorization: "Bearer " + mainToken,
        },
      });

      if (response.data.ok) {
        appContext.handleUser(response.data.data);
      }
    } catch (e) {
      console.error("Failed to fetch user profile:", e);
    } finally {
      appContext.handleLoading(false);
      setShowWelcome(false);
      navigate(`/${i18next.language}/dashboard`);
    }
  };

  const handleCloseLoginModal = () => {
    setShowLoginModal(false);
  };

  if (isValidating) {
    return (
      <div className="flex items-center justify-center w-full h-screen">
        <Spin size="large" />
      </div>
    );
  }

  if (showWelcome) {
    return (
      <>
        <div className="flex flex-col items-center justify-center w-full h-screen bg-gradient-to-b from-gray-50 to-gray-100">
          <div className="w-11/12 max-w-md p-8 bg-white shadow-sm rounded-2xl">
            <div className="mb-6 text-center">
              <h1 className="mb-2 text-3xl font-bold text-gray-800">Welcome to Shaku</h1>
              <p className="text-sm text-gray-600">Choose how you'd like to continue</p>
            </div>

            <div className="space-y-4">
              <Button type="primary" size="large" className="w-full h-12 font-semibold text-white bg-primary hover:bg-primary/90" onClick={handleShowLogin}>
                Sign In
              </Button>

              <Button type="default" size="large" className="w-full h-12 font-semibold text-gray-700 bg-white border-2 border-gray-300 hover:bg-gray-50" onClick={handleContinueWithoutLogin}>
                Continue Without Sign In
              </Button>
            </div>

            <div className="mt-6 text-xs text-center text-gray-500">
              <p>By continuing, you agree to our Terms of Service and Privacy Policy</p>
            </div>
          </div>
        </div>

        <LoginFormModal visible={showLoginModal} onClose={handleCloseLoginModal} onSuccess={handleLoginSuccess} />
      </>
    );
  }

  return null;
};

export default Gate;
